import { colorForPrecipValue } from "./PrecipMarker";
import { observer } from "mobx-react-lite";
import {
  sensorOverlapId,
  useMarkerPosition,
} from "../contexts/MarkerOverlapContext";
import { useRootStore } from "../models/Root";
import { IPrecipSensor } from "../models/PrecipSensor";
import { ISensorDisplayPrecip } from "../models/SensorDisplay";
import convert from "../convert";
import config from "../config";
import precipDotRadiusForZoom from "./precipDotRadius";
import { compareSensorMarkerOrder } from "./precipMarkerOrder";
import { collapseDuplicateSensors } from "./duplicateSensors";
import { compactMarkerPosition } from "../shared/markerOverlap";
import SensorMarker, { SensorMarkerLayer } from "./SensorMarker";
import { decimalPlaces, useLocaleContext } from "../contexts/LocaleContext";
import { scaleThreshold } from "d3-scale";

interface PrecipSensorMarkerProps {
  sensor: IPrecipSensor;
  display: ISensorDisplayPrecip;
  dotRadius: number;
  paintOrder: number;
  useOriginalPosition?: boolean;
}

const PrecipSensorMarker = observer(
  ({
    sensor,
    display,
    dotRadius,
    paintOrder,
    useOriginalPosition = false,
  }: PrecipSensorMarkerProps) => {
    const { interval, measure } = display;
    const {
      menus: { panel },
      sensors: { setSensor, selected },
      layers: { baseMap },
      map: { zoom },
    } = useRootStore();
    const {
      units: {
        precip: { total: toTotalUnit, intensity: toIntensityUnit },
      },
    } = useLocaleContext();
    const isSelected = selected?.sensor.id === sensor.id;
    const nudgedPosition = useMarkerPosition(
      sensorOverlapId(sensor.type, sensor.id),
      sensor.position
    );
    const position = useOriginalPosition ? sensor.position : nudgedPosition;
    const value =
      measure === "total"
        ? sensor.totalFor(interval)
        : sensor.intensityFor(interval);
    const fromUnit =
      measure === "total" ? sensor.totalUnit : sensor.intensityUnit;
    const onClick = panel === "measure" ? undefined : () => setSensor(sensor);
    const markerColor = baseMap.startsWith("Imagery") ? "white" : "black";
    if (sensor.latest === null) {
      return (
        <SensorMarker
          position={position}
          sensorType="precip"
          kind="missing"
          fillColor={config.markers.missing || markerColor}
          color={markerColor}
          radius={dotRadius}
          fillOpacity={config.markers.missing ? 1 : 0.8}
          strokeOpacity={config.markers.missing ? 1 : 0.8}
          selected={isSelected}
          paintOrder={paintOrder}
          onClick={onClick}
        />
      );
    }
    const hasValue = value !== null && value !== 0;
    const dotColor = hasValue
      ? colorForPrecipValue(
          convert(value)
            .from(fromUnit)
            .to(measure === "total" ? "in" : "in/h")
        )
      : markerColor;
    const dotDisplayZoomLevel = config.markers.precip.dotDisplayZoomLevel;
    const showDot =
      value === null ||
      (dotDisplayZoomLevel !== false &&
        (value === 0 || zoom <= dotDisplayZoomLevel));
    if (showDot) {
      const dotPosition = useOriginalPosition
        ? position
        : compactMarkerPosition(sensor.position, position, dotRadius * 2);
      return (
        <SensorMarker
          position={dotPosition}
          sensorType="precip"
          kind="dot"
          fillColor={dotColor}
          color={hasValue ? "black" : markerColor}
          radius={dotRadius}
          fillOpacity={hasValue ? 1 : 0.1}
          strokeOpacity={hasValue ? 1 : 0.8}
          selected={isSelected}
          paintOrder={paintOrder}
          onClick={onClick}
        />
      );
    }
    const toUnit = measure === "total" ? toTotalUnit : toIntensityUnit;
    const label = convert(value)
      .from(fromUnit)
      .to(toUnit)
      .toFixed(decimalPlaces(toUnit));
    const { breakValues, backgroundColors, textColors } =
      config.markers.precip.tag;
    const backgroundColor = scaleThreshold(breakValues, backgroundColors);
    const textColor = scaleThreshold(breakValues, textColors);
    const colorValue = convert(value)
      .from(fromUnit)
      .to(measure === "total" ? "in" : "in/h");

    return (
      <SensorMarker
        position={position}
        sensorType="precip"
        kind="tag"
        fillColor={backgroundColor(colorValue)}
        color={textColor(colorValue)}
        label={label}
        selected={isSelected}
        paintOrder={paintOrder}
        onClick={onClick}
      />
    );
  }
);

const PrecipSensorsLayer = ({ display }: { display: ISensorDisplayPrecip }) => {
  const {
    sensors: { precipSensors },
    map: { zoom },
  } = useRootStore();
  const interval = display.interval;
  const valueForSensor = (sensor: IPrecipSensor) =>
    display.measure === "total"
      ? sensor.totalFor(interval)
      : sensor.intensityFor(interval);
  const sorter = (left: IPrecipSensor, right: IPrecipSensor) =>
    compareSensorMarkerOrder(
      { missing: left.latest === null, value: valueForSensor(left) },
      { missing: right.latest === null, value: valueForSensor(right) }
    );
  const sensors = precipSensors.sort(sorter);
  const duplicateSiteExpandZoomLevel =
    config.markers.precip.duplicateSiteExpandZoomLevel;
  const displaySensors =
    duplicateSiteExpandZoomLevel !== false &&
    zoom < duplicateSiteExpandZoomLevel
      ? collapseDuplicateSensors(sensors)
      : sensors.map((sensor) => ({ sensor, useOriginalPosition: false }));
  const dotDisplayZoomLevel = config.markers.precip.dotDisplayZoomLevel;
  const dotRadius =
    dotDisplayZoomLevel === false
      ? 7
      : precipDotRadiusForZoom(zoom, dotDisplayZoomLevel);

  return (
    <SensorMarkerLayer>
      {displaySensors.map(({ sensor, useOriginalPosition }, paintOrder) => (
        <PrecipSensorMarker
          key={sensor.id}
          sensor={sensor}
          display={display}
          dotRadius={dotRadius}
          paintOrder={paintOrder}
          useOriginalPosition={useOriginalPosition}
        />
      ))}
    </SensorMarkerLayer>
  );
};

export default observer(PrecipSensorsLayer);
